<template>
  <div>
    <div
      v-if="loading"
      class="d-flex justify-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-else-if="!entries || entries.length === 0"
      class="text-medium-emphasis text-body-2 py-8 text-center"
    >
      {{ emptyText }}
    </div>

    <v-list
      v-else
      density="compact"
      class="audit-entry-list"
    >
      <v-list-item
        v-for="entry in entries"
        :key="entry.id"
        class="audit-entry"
        @click="openDetail(entry)"
      >
        <template #prepend>
          <v-icon
            :color="actionColor(entry.action)"
            class="mr-2"
          >
            {{ actionIcon(entry.action) }}
          </v-icon>
        </template>
        <v-list-item-title>{{ entryTitle(entry) }}</v-list-item-title>
        <v-list-item-subtitle>
          <span v-if="entrySummary(entry)">{{ entrySummary(entry) }} &middot; </span>{{ entryMeta(entry) }}
        </v-list-item-subtitle>
        <template #append>
          <v-icon size="small">
            mdi-chevron-right
          </v-icon>
        </template>
      </v-list-item>
    </v-list>

    <v-dialog
      v-model="detailOpen"
      max-width="720px"
      scrollable
    >
      <v-card v-if="selected">
        <v-card-title class="text-h6 audit-detail-header">
          {{ entryTitle(selected) }}
          <v-btn
            icon
            variant="text"
            class="audit-detail-close"
            @click="detailOpen = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-body-2 text-medium-emphasis mb-4">
            {{ entryMeta(selected) }}
          </div>

          <template v-if="detail.kind === 'diff'">
            <v-table density="compact">
              <thead>
                <tr>
                  <th class="text-left">
                    Field
                  </th>
                  <th class="text-left">
                    From
                  </th>
                  <th class="text-left">
                    To
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in detail.rows"
                  :key="row.field"
                >
                  <td class="font-weight-medium">
                    {{ row.field }}
                  </td>
                  <td class="text-medium-emphasis">
                    {{ row.from }}
                  </td>
                  <td>{{ row.to }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>

          <template v-else-if="detail.kind === 'snapshot'">
            <v-table density="compact">
              <tbody>
                <tr
                  v-for="row in detail.rows"
                  :key="row.field"
                >
                  <td class="font-weight-medium">
                    {{ row.field }}
                  </td>
                  <td>{{ row.value }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>

          <template v-else>
            <pre class="audit-json">{{ detail.text }}</pre>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="detailOpen = false">
            <v-icon>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const ACTION_VERB = { create: "Created", update: "Updated", delete: "Deleted", merge: "Merged", archive: "Archived", restore: "Restored" };
const ACTION_ICON = {
  create: "mdi-plus-circle",
  update: "mdi-pencil",
  delete: "mdi-delete",
  merge: "mdi-call-merge",
  archive: "mdi-archive-arrow-down",
  restore: "mdi-archive-arrow-up",
};
const ACTION_COLOR = { create: "success", update: "primary", delete: "error", merge: "info", archive: "warning", restore: "success" };

export default {
  name: "AuditLogPanel",
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: "No audit entries.",
    },
  },
  data() {
    return {
      detailOpen: false,
      selected: null,
    };
  },
  computed: {
    detail() {
      const entry = this.selected;
      if (!entry || !entry.changes) {
        return { kind: "json", text: "{}" };
      }
      const changes = entry.changes;

      if (entry.action === "merge" && changes.survivor) {
        const rows = [
          { field: "Survivor", value: this.formatValue(changes.survivor) },
          { field: "Duplicate (archived)", value: this.formatValue(changes.duplicate) },
          { field: "Sightings reassigned", value: this.formatValue(changes.sightings_reassigned) },
          { field: "Images reassigned", value: this.formatValue(changes.images_reassigned) },
          { field: "Mother links repointed", value: this.formatValue(changes.mother_refs_repointed) },
        ];
        const overrides = changes.overrides_applied || {};
        Object.keys(overrides).forEach((field) => {
          const diff = overrides[field] || {};
          rows.push({
            field: `Field: ${this.prettyField(field)}`,
            value: `${this.formatValue(diff.from)} \u2192 ${this.formatValue(diff.to)}`,
          });
        });
        return { kind: "snapshot", rows };
      }

      if (changes.archived !== undefined) {
        const rows = [
          { field: "Status", value: changes.archived ? "Archived" : "Restored" },
        ];
        if (changes.rows !== undefined) {
          rows.push({ field: "Rows affected", value: this.formatValue(changes.rows) });
        }
        return { kind: "snapshot", rows };
      }

      if (changes.changed && typeof changes.changed === "object") {
        const rows = Object.keys(changes.changed).map((field) => {
          const diff = changes.changed[field] || {};
          return {
            field: this.prettyField(field),
            from: this.formatValue(diff.from),
            to: this.formatValue(diff.to),
          };
        });
        return { kind: "diff", rows };
      }

      const snapshot = changes.after || changes.before;
      if (snapshot && typeof snapshot === "object" && !Array.isArray(snapshot)) {
        const rows = Object.keys(snapshot).map((field) => ({
          field: this.prettyField(field),
          value: this.formatValue(snapshot[field]),
        }));
        return { kind: "snapshot", rows };
      }

      if (changes.before_rows !== undefined || changes.after_rows !== undefined) {
        const rows = [
          { field: "Rows before", value: this.formatValue(changes.before_rows) },
          { field: "Rows after", value: this.formatValue(changes.after_rows) },
          { field: "Whales", value: this.formatValue(changes.whale_ids) },
        ];
        return { kind: "snapshot", rows };
      }

      return { kind: "json", text: JSON.stringify(changes, null, 2) };
    },
  },
  methods: {
    openDetail(entry) {
      this.selected = entry;
      this.detailOpen = true;
    },
    actionIcon(action) {
      return ACTION_ICON[action] || "mdi-circle-small";
    },
    actionColor(action) {
      return ACTION_COLOR[action] || "grey";
    },
    actionVerb(action) {
      return ACTION_VERB[action] || (action || "Changed");
    },
    entryTitle(entry) {
      const verb = this.actionVerb(entry.action);
      const type = entry.entityType || "record";
      const label = entry.entityLabel ? ` \u2014 ${entry.entityLabel}` : "";
      return `${verb} ${type}${label}`;
    },
    entrySummary(entry) {
      const changes = entry.changes;
      if (!changes) return "";

      if (entry.action === "merge" && changes.survivor) {
        return `${changes.duplicate} \u2192 ${changes.survivor}`;
      }

      if (changes.archived !== undefined) {
        return changes.rows !== undefined ? `${changes.rows} rows` : "";
      }

      if (changes.changed && typeof changes.changed === "object") {
        const fields = Object.keys(changes.changed).map((f) => this.prettyField(f));
        return fields.length ? `Changed ${fields.join(", ")}` : "";
      }

      if (changes.before_rows !== undefined || changes.after_rows !== undefined) {
        return `${changes.before_rows} \u2192 ${changes.after_rows} rows`;
      }

      const snapshot = changes.after || changes.before;
      if (snapshot && typeof snapshot === "object") {
        if (snapshot.rows !== undefined) {
          const ids = Array.isArray(snapshot.whale_ids) ? snapshot.whale_ids : [];
          const idText = ids.length ? ` \u00b7 ${ids.join(", ")}` : "";
          return `${snapshot.rows} row${snapshot.rows === 1 ? "" : "s"}${idText}`;
        }
        return entry.action === "delete" ? "Record removed" : "New record";
      }

      return "";
    },
    entryMeta(entry) {
      const when = this.formatTime(entry.createdAt);
      const who = entry.userId || "unknown user";
      return `${when} \u00b7 ${who}`;
    },
    formatTime(iso) {
      if (!iso) return "";
      const date = new Date(iso);
      return Number.isNaN(date.getTime()) ? String(iso) : date.toLocaleString();
    },
    formatValue(value) {
      if (value === null || value === undefined || value === "") return "\u2014";
      if (Array.isArray(value)) return value.length ? value.join(", ") : "\u2014";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    prettyField(key) {
      if (!key) return "";
      return String(key)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
  },
};
</script>

<style scoped>
.audit-entry {
  cursor: pointer;
}

.audit-entry-list {
  max-height: 60vh;
  overflow-y: auto;
}

.audit-detail-header {
  display: flex;
  align-items: center;
}

.audit-detail-close {
  margin-left: auto;
}

.audit-json {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.04);
  padding: 12px;
  border-radius: 4px;
}
</style>
